/**
 * Capacitor native shell bootstrap.
 *
 * Runs exactly once on app startup. On the web build every call short-
 * circuits so nothing changes for browser users.
 *
 *   1. Hide the launch splash after React has mounted (avoids the flash of
 *      unstyled content that would otherwise show).
 *   2. Style the status bar to match the black theme.
 *   3. Listen for `appUrlOpen` deep links (OAuth returns, marketing emails
 *      linking into `/dashboard`, etc.) and hand them off to react-router
 *      via `window.location.assign()`.
 */

import { App as CapApp } from '@capacitor/app';
import { SplashScreen } from '@capacitor/splash-screen';
import { StatusBar, Style } from '@capacitor/status-bar';
import { isNative, isAndroid } from './platform';

let bootstrapped = false;

export function bootstrapNativeShell() {
  if (bootstrapped || !isNative()) return;
  bootstrapped = true;

  // Status bar — match the site's black brand
  (async () => {
    try {
      await StatusBar.setStyle({ style: Style.Dark });
      if (isAndroid()) {
        await StatusBar.setBackgroundColor({ color: '#000000' });
      }
    } catch (_) {}
  })();

  // Hide splash once React is up
  setTimeout(async () => {
    try { await SplashScreen.hide({ fadeOutDuration: 200 }); } catch (_) {}
  }, 350);

  // Deep links — convert incoming universal-link / app-link URLs into
  // in-app navigations. Example: `https://connectify-esims.com/dashboard`
  // opens the app straight onto the dashboard.
  CapApp.addListener('appUrlOpen', (event) => {
    try {
      const url = new URL(event.url);
      // Strip the origin — keep the path + query so react-router picks it up.
      const target = `${url.pathname}${url.search || ''}${url.hash || ''}`;
      if (target && target !== '/') {
        window.location.assign(target);
      }
    } catch (_) {
      // Non-standard URL scheme — ignore
    }
  });
}
